import IonIcon from '@reacticons/ionicons'
import { useRouter } from 'next/router'
import React, { useEffect, useRef, useState } from 'react'
import { CheckBox } from 'src/components/global/CheckBox/CheckBox'
import { INITIAL_ITEM_CNT } from 'src/config/config'
import { useUpdateQueryParams } from 'src/hooks/useUpdateQueryParams'
import { FilterOptionItem, TaxonomyType } from 'src/types'
import { getTaxonomyQueryFieldName } from 'src/utils/getTaxonomyQueryFieldName'

interface AccordionProps {
  title: TaxonomyType
  items: FilterOptionItem[]
  opened: boolean
  disabled?: boolean
  showCheckbox?: boolean
  showSearchbox?: null | string[]
  onSelectAccordion: (title: TaxonomyType) => void
  onClickWhenDisabled?: () => void
}

export const Accordion: React.FC<AccordionProps> = ({
  title,
  items,
  opened,
  showCheckbox = false,
  disabled = false,
  showSearchbox = null,
  onSelectAccordion,
  onClickWhenDisabled,
}) => {
  // const [data, setData] = useState<FilterOptionItem[]>(items)
  const [data, setData] = useState<FilterOptionItem[]>(
    (items || []).filter((item) => item?.name?.toLowerCase().trim() !== 'N/A'.toLowerCase()),
  )

  const [searchTerm, setSearchTerm] = useState('')
  const [filteredData, setFilteredData] = useState<FilterOptionItem[]>([])

  const router = useRouter()
  const { updateQueryParams } = useUpdateQueryParams()
  const contentSpace = useRef(null)
  const [showAll, setShowAll] = useState(false)
  const handleShowAll = () => {
    if (disabled) {
      onClickWhenDisabled && onClickWhenDisabled()
      return
    }
    setShowAll(!showAll)
  }

  // Filter data based on search term
  useEffect(() => {
    if (searchTerm.trim() === '') {
      setFilteredData(data)
    } else {
      const filtered = data.filter((item) => item.name.toLowerCase().includes(searchTerm.toLowerCase()))
      setFilteredData(filtered)
    }
  }, [data, searchTerm])

  useEffect(() => {
    setData(
      (items || [])
        .filter((item) => item?.name?.toLowerCase().trim() !== 'N/A'.toLowerCase())
        .map((item, index) => ({
          ...item,
          selected: data[index] && data[index].selected,
        })),
    )
  }, [items])

  useEffect(() => {
    const param = getTaxonomyQueryFieldName(title)
    if (!param) return
    if (router.query[param] !== undefined) {
      const checks = (router.query[param] as string).split(',')
      setData(
        data.map((item) => {
          item.selected = !!checks.find((c) => c.split('-').join(',') === item.id)
          return item
        }),
      )
    } else {
      setData(
        data.map((item) => {
          item.selected = false
          return item
        }),
      )
    }
  }, [router, opened])

  const toggleAccordion = () => {
    if (disabled) {
      onClickWhenDisabled && onClickWhenDisabled()
      return
    }
    onSelectAccordion(title)
  }

  const onChanged = (checked: boolean, value?: string) => {
    if (disabled) {
      onClickWhenDisabled && onClickWhenDisabled()
      return
    }
    const param = getTaxonomyQueryFieldName(title)
    if (!param) return

    const newData = data.map((item) => {
      if (item.id === value) {
        item.selected = checked
      }
      return item
    })
    setData(newData)
    const queryValue = newData
      .filter((item) => item.selected)
      .map((item) => item.id.split(',').join('-'))
      .join(',')

    if (!queryValue) {
      updateQueryParams({ [param]: undefined, page: undefined })
    } else {
      updateQueryParams({ [param]: queryValue, page: undefined })
    }
  }

  const sortItems = (filterItems: FilterOptionItem[]) => {
    /* if (filterItems && filterItems.length > 0) {
      return filterItems.sort((a, b) => a.name.localeCompare(b.name))
    } */
    return filterItems
  }

  const handleSearchChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setSearchTerm(e.target.value)
    setShowAll(true) // Show all items when searching
  }

  const clearSearch = () => {
    setSearchTerm('')
    setShowAll(false)
  }

  return (
    <div className="w-full flex flex-col">
      <button
        className="py-3 box-border appearance-none cursor-pointer focus:outline-none flex items-center justify-between"
        onClick={toggleAccordion}
      >
        <p className="inline-block text-footnote light  font-bold font-primary text-textdark uppercase text-[0.94rem]">
          {title === TaxonomyType.DocumentType ? 'RESOURCE TYPE' : title}
        </p>
        {opened ? <IonIcon name="chevron-up-outline" /> : <IonIcon name="chevron-down-outline" />}
      </button>
      <div
        ref={contentSpace}
        style={{ maxHeight: opened ? `380px` : '0px' }}
        className="transition-max-height duration-500 overflow-hidden overflow-y-scroll ease-in-out"
      >
        {/* Search field - only show when accordion is opened */}
        {opened && showSearchbox && showSearchbox.includes(title) && (
          <div className="px-4 py-2 border-b border-gray-200">
            <div className="relative">
              <input
                type="text"
                placeholder="Search..."
                value={searchTerm}
                onChange={handleSearchChange}
                className="w-full px-3 py-2 text-sm border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                disabled={disabled}
              />
              {searchTerm && (
                <button
                  onClick={clearSearch}
                  className="absolute right-2 top-1/2 transform -translate-y-1/2 text-gray-400 hover:text-gray-600"
                >
                  <IonIcon name="close-outline" className="w-4 h-4" />
                </button>
              )}
            </div>
          </div>
        )}

        {filteredData &&
          sortItems(filteredData).map((item, index: number) => {
            let clsName = ''
            if (index === 0) {
              clsName = 'pb-4 pl-[1.25rem] font-bold font-primary text-textdark text-[0.94rem] mt-[12px]'
            } else if (index === filteredData.length - 1) {
              clsName = 'pb-4 pl-[1.25rem] font-bold font-primary text-textdark text-[0.94rem] mb-[12px]'
            } else {
              clsName = 'pb-4 pl-[1.25rem] font-bold font-primary text-textdark text-[0.94rem]'
            }

            // Show all items when searching or when showAll is true
            const shouldShow = searchTerm || index < INITIAL_ITEM_CNT || showAll

            if (shouldShow) {
              return (
                item?.name?.toLowerCase().trim() !== 'N/A'.toLowerCase() && (
                  <div key={index} className={clsName}>
                    {showCheckbox && (
                      <CheckBox
                        key={index}
                        label={item.name}
                        value={item.id}
                        onChanged={onChanged}
                        selected={!!item.selected}
                        indeterminate={!!item.selected}
                      />
                    )}
                  </div>
                )
              )
            } else {
              return <div key={index}></div>
            }
          })}
        {filteredData && filteredData.length > INITIAL_ITEM_CNT && !showAll && !searchTerm && (
          <div
            className="pb-4 pl-[1.25rem] font-primary text-textdark text-[0.94rem] underline cursor-pointer"
            onClick={handleShowAll}
          >
            +{filteredData.length - INITIAL_ITEM_CNT} {'more'}
          </div>
        )}

        {/* Show "no results" message when searching and no items found */}
        {searchTerm && filteredData.length === 0 && (
          <div className="px-4 py-4 text-sm text-gray-500 text-center">No items found matching "{searchTerm}"</div>
        )}
      </div>
    </div>
  )
}
